import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';

export interface TreeNode {
	name: string;
	children?: TreeNode[];
	[key: string]: unknown;
}

export interface TreeLevelFilter {
	value: string;
	level: number;
	field?: string;
}

interface FlatTreeNode {
	data: TreeNode;
	expandable: boolean;
	level: number;
	name: string;
}

@Component({
	selector: 'app-tree',
	templateUrl: './tree.component.html',
	styleUrls: ['./tree.component.scss']
})
export class TreeComponent {
	private nodes: TreeNode[] = [];
	private globalFilterValue = '';
	private levelFilterValue: TreeLevelFilter | null = null;
	private selectedIdsValue = new Set<string>();
	private visibleNodes = new Set<TreeNode>();

	@Input() set data(nodes: TreeNode[] | null | undefined) {
		this.nodes = nodes || [];
		this.dataSource.data = this.nodes;
		this.checklistSelection.clear();
		this.syncSelection();
		this.updateFilter();
	}

	@Input() set selectedIds(ids: unknown[] | null | undefined) {
		this.selectedIdsValue = new Set((ids || []).map(id => String(id)));
		this.syncSelection();
	}

	@Input() set globalFilter(value: string | null | undefined) {
		this.globalFilterValue = value || '';
		this.updateFilter();
	}

	@Input() set levelFilter(value: TreeLevelFilter | null | undefined) {
		this.levelFilterValue = value?.value ? value : null;
		this.updateFilter();
	}

	@Output() selectionChange = new EventEmitter<TreeNode[]>();

	private transformer = (node: TreeNode, level: number): FlatTreeNode => ({
		data: node,
		expandable: !!node.children?.length,
		level,
		name: node.name
	});

	treeFlattener = new MatTreeFlattener<TreeNode, FlatTreeNode>(
		this.transformer,
		node => node.level,
		node => node.expandable,
		node => node.children
	);

	treeControl = new FlatTreeControl<FlatTreeNode>(
		node => node.level,
		node => node.expandable
	);

	dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
	checklistSelection = new SelectionModel<FlatTreeNode>(true);

	hasChild = (_: number, node: FlatTreeNode): boolean => node.expandable;

	isNodeVisible(node: TreeNode): boolean {
		return this.visibleNodes.has(node);
	}

	private updateFilter(): void {
		const globalValue = this.globalFilterValue.trim().toLocaleLowerCase();
		const levelFilter = this.levelFilterValue;
		const globalMatches = new Set<TreeNode>();
		const levelMatches = new Set<TreeNode>();

		const visit = (nodes: TreeNode[], level: number): void => {
			for (const node of nodes) {
				const children = node.children || [];
				visit(children, level + 1);

				const nameMatches = globalValue
					&& node.name.toLocaleLowerCase().includes(globalValue);
				if (nameMatches) {
					this.addSubtree(globalMatches, node);
				}
				if (children.some(child => globalMatches.has(child))) {
					globalMatches.add(node);
				}

				const field = levelFilter?.field || 'name';
				const fieldValue = node[field];
				if (levelFilter && level === levelFilter.level
					&& String(fieldValue ?? '') === levelFilter.value) {
					this.addSubtree(levelMatches, node);
				}
				if (children.some(child => levelMatches.has(child))) {
					levelMatches.add(node);
				}
			}
		};

		visit(this.nodes, 0);
		this.visibleNodes.clear();
		const addVisibleNodes = (nodes: TreeNode[]): void => {
			for (const node of nodes) {
				if ((!globalValue || globalMatches.has(node))
					&& (!levelFilter || levelMatches.has(node))) {
					this.visibleNodes.add(node);
				}
				addVisibleNodes(node.children || []);
			}
		};
		addVisibleNodes(this.nodes);

		if (globalValue || levelFilter) {
			for (const node of this.treeControl.dataNodes) {
				if (this.visibleNodes.has(node.data) && node.expandable) {
					this.treeControl.expand(node);
				}
			}
		}
	}

	private addSubtree(nodes: Set<TreeNode>, node: TreeNode): void {
		nodes.add(node);
		for (const child of node.children || []) {
			this.addSubtree(nodes, child);
		}
	}

	descendantsAllSelected(node: FlatTreeNode): boolean {
		const descendants = this.treeControl.getDescendants(node);
		return descendants.length > 0 && descendants.every(child =>
			this.checklistSelection.isSelected(child)
		);
	}

	descendantsPartiallySelected(node: FlatTreeNode): boolean {
		const descendants = this.treeControl.getDescendants(node);
		return descendants.some(child => this.checklistSelection.isSelected(child))
			&& !this.descendantsAllSelected(node);
	}

	itemSelectionToggle(node: FlatTreeNode): void {
		this.checklistSelection.toggle(node);
		const descendants = this.treeControl.getDescendants(node);
		this.checklistSelection.isSelected(node)
			? this.checklistSelection.select(...descendants)
			: this.checklistSelection.deselect(...descendants);
		this.checkAllParentsSelection(node);
		this.emitSelectionChange();
	}

	leafItemSelectionToggle(node: FlatTreeNode): void {
		this.checklistSelection.toggle(node);
		this.checkAllParentsSelection(node);
		this.emitSelectionChange();
	}

	private checkAllParentsSelection(node: FlatTreeNode): void {
		let parent = this.getParentNode(node);
		while (parent) {
			this.checkRootNodeSelection(parent);
			parent = this.getParentNode(parent);
		}
	}

	private checkRootNodeSelection(node: FlatTreeNode): void {
		const nodeSelected = this.checklistSelection.isSelected(node);
		const descendants = this.treeControl.getDescendants(node);
		const descendantsAllSelected = descendants.every(child =>
			this.checklistSelection.isSelected(child)
		);

		if (nodeSelected && !descendantsAllSelected) {
			this.checklistSelection.deselect(node);
		} else if (!nodeSelected && descendantsAllSelected) {
			this.checklistSelection.select(node);
		}
	}

	private getParentNode(node: FlatTreeNode): FlatTreeNode | null {
		if (node.level < 1) {
			return null;
		}

		const flatNodes = this.treeControl.dataNodes;
		for (let index = flatNodes.indexOf(node) - 1; index >= 0; index--) {
			const parent = flatNodes[index];
			if (parent.level < node.level) {
				return parent;
			}
		}
		return null;
	}

	private emitSelectionChange(): void {
		this.selectionChange.emit(this.checklistSelection.selected.map(node => node.data));
	}

	private syncSelection(): void {
		this.checklistSelection.clear();
		const selectedNodes = this.treeControl.dataNodes.filter(node =>
			this.selectedIdsValue.has(String(node.data['id']))
		);
		this.checklistSelection.select(...selectedNodes);
		selectedNodes.forEach(node => this.checkAllParentsSelection(node));
	}
}